import React, { useState, useEffect, useMemo } from 'react';
import {
  Image as ImageIcon,
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ExternalLink,
  Layers,
  ArrowUpDown,
  Search,
  Upload,
  Eye,
  Sliders,
  AlertCircle
} from 'lucide-react';
import { bannersAPI } from '../utils/api';
import AssetPreview, { resolveAssetUrl } from '../components/AssetPreview';

const BANNERS_TYPES = [
  { key: 'ALL', label: 'All Banners' },
  { key: 'HOME', label: 'Home Screen Banners' },
  { key: 'PROFILE', label: 'Profile Bottom Banners' }
];

const ACTION_TYPES = [
  { key: 'NONE', label: 'No Action' },
  { key: 'EXTERNAL_URL', label: 'Open External URL' },
  { key: 'ROOM', label: 'Redirect to Live Room' },
  { key: 'PROFILE', label: 'Open User Profile' },
  { key: 'STORE', label: 'Open Store Asset' },
  { key: 'RECHARGE', label: 'Open Coin Recharge' }
];

const DISALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.zip', '.rar', '.exe'];

const BannersManagement = () => {
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState({ total: 0, home_count: 0, profile_count: 0, active_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [editBannerId, setEditBannerId] = useState(null);

  const [formData, setFormData] = useState({
    banner_type: 'HOME',
    title: '',
    description: '',
    action_type: 'NONE',
    link_url: '',
    target_id: '',
    sort_order: 0,
    is_active: true,
    file: null,
    existing_image: ''
  });

  const [previewModalUrl, setPreviewModalUrl] = useState(null);
  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3500);
  };

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await bannersAPI.getAdminBanners();
      if (res.data && res.data.success) {
        setBanners(res.data.data || []);
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      } else {
        showToast(res.data?.message || 'Failed to load banners', 'error');
      }
    } catch (err) {
      console.error('Error fetching banners:', err);
      showToast('Error connecting to banners API', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleOpenCreateModal = (type = 'HOME') => {
    setEditBannerId(null);
    setModalError('');
    setFormData({
      banner_type: type === 'ALL' ? 'HOME' : type,
      title: '',
      description: '',
      action_type: 'NONE',
      link_url: '',
      target_id: '',
      sort_order: banners.length ? Math.max(...banners.map(b => b.sort_order || 0)) + 1 : 0,
      is_active: true,
      file: null,
      existing_image: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (banner) => {
    setEditBannerId(banner.id);
    setModalError('');
    setFormData({
      banner_type: banner.banner_type || 'HOME',
      title: banner.title || '',
      description: banner.description || '',
      action_type: banner.action_type || 'NONE',
      link_url: banner.link_url || '',
      target_id: banner.target_id || '',
      sort_order: banner.sort_order || 0,
      is_active: banner.is_active !== undefined ? banner.is_active : true,
      file: null,
      existing_image: banner.image_url || ''
    });
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isDisallowed = DISALLOWED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
    if (isDisallowed) {
      setModalError(`Documents (${file.name.split('.').pop()}) cannot be uploaded. Please choose an image or GIF.`);
      return;
    }

    setModalError('');
    setFormData(prev => ({
      ...prev,
      file: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setModalError('Banner title is required');
      return;
    }

    if (!editBannerId && !formData.file) {
      setModalError('Please select a banner image or GIF to upload');
      return;
    }

    setModalLoading(true);
    setModalError('');

    try {
      const data = new FormData();
      data.append('banner_type', formData.banner_type);
      data.append('title', formData.title.trim());
      data.append('description', formData.description.trim());
      data.append('action_type', formData.action_type);
      data.append('link_url', formData.link_url.trim());
      data.append('target_id', formData.target_id.trim());
      data.append('sort_order', formData.sort_order);
      data.append('is_active', formData.is_active ? 'true' : 'false');

      if (formData.file) {
        data.append('file', formData.file);
      }

      let res;
      if (editBannerId) {
        res = await bannersAPI.updateBanner(editBannerId, data);
      } else {
        res = await bannersAPI.createBanner(data);
      }

      if (res.data && res.data.success) {
        showToast(editBannerId ? 'Banner updated successfully' : 'Banner uploaded successfully', 'success');
        setIsModalOpen(false);
        fetchBanners();
      } else {
        setModalError(res.data?.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error saving banner:', err);
      setModalError(err.response?.data?.message || 'Failed to save banner');
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleStatus = async (banner) => {
    try {
      const updatedStatus = !banner.is_active;
      const data = new FormData();
      data.append('is_active', updatedStatus ? 'true' : 'false');

      const res = await bannersAPI.updateBanner(banner.id, data);
      if (res.data && res.data.success) {
        setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, is_active: updatedStatus } : b));
        setStats(prev => ({
          ...prev,
          active_count: updatedStatus ? prev.active_count + 1 : Math.max(0, prev.active_count - 1)
        }));
        showToast(`Banner ${updatedStatus ? 'activated' : 'deactivated'}`, 'success');
      } else {
        showToast(res.data?.message || 'Failed to update status', 'error');
      }
    } catch (err) {
      console.error('Error toggling banner status:', err);
      showToast('Error toggling banner status', 'error');
    }
  };

  const handleDeleteBanner = async (banner) => {
    if (!window.confirm(`Are you sure you want to delete banner "${banner.title}"?`)) {
      return;
    }

    try {
      const res = await bannersAPI.deleteBanner(banner.id);
      if (res.data && res.data.success) {
        showToast('Banner deleted successfully', 'success');
        fetchBanners();
      } else {
        showToast(res.data?.message || 'Failed to delete banner', 'error');
      }
    } catch (err) {
      console.error('Error deleting banner:', err);
      showToast('Error deleting banner', 'error');
    }
  };

  const filteredBanners = useMemo(() => {
    return banners.filter(b => {
      // Tab filter
      if (activeTab !== 'ALL' && b.banner_type !== activeTab) {
        return false;
      }
      // Status filter
      if (statusFilter === 'ACTIVE' && !b.is_active) return false;
      if (statusFilter === 'INACTIVE' && b.is_active) return false;

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchTitle = (b.title || '').toLowerCase().includes(query);
        const matchDesc = (b.description || '').toLowerCase().includes(query);
        const matchLink = (b.link_url || '').toLowerCase().includes(query);
        const matchTarget = (b.target_id || '').toLowerCase().includes(query);
        if (!matchTitle && !matchDesc && !matchLink && !matchTarget) {
          return false;
        }
      }
      return true;
    });
  }, [banners, activeTab, statusFilter, searchQuery]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 text-white font-medium transition-all ${toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
          }`}>
          {toast.type === 'error' ? <XCircle size={20} /> : <CheckCircle2 size={20} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-indigo-100">
            <ImageIcon size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Banner Management</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Upload and manage Home Page sliders and Profile bottom banners for mobile app
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchBanners}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            title="Refresh List"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>

          <button
            onClick={() => handleOpenCreateModal(activeTab)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl shadow-md shadow-indigo-200 font-semibold text-sm transition"
          >
            <Plus size={18} />
            <span>Upload New Banner</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Banners</p>
            <p className="text-2xl font-extrabold text-slate-800 mt-1">{stats.total}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
            <Layers size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-blue-500 uppercase tracking-wider">Home Banners</p>
            <p className="text-2xl font-extrabold text-blue-700 mt-1">{stats.home_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <ImageIcon size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-purple-500 uppercase tracking-wider">Profile Banners</p>
            <p className="text-2xl font-extrabold text-purple-700 mt-1">{stats.profile_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
            <Sliders size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider">Active Banners</p>
            <p className="text-2xl font-extrabold text-emerald-700 mt-1">{stats.active_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 size={22} />
          </div>
        </div>
      </div>

      {/* Tabs and Controls */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Tabs */}
          <div className="flex p-1 bg-slate-100 rounded-xl w-full sm:w-auto">
            {BANNERS_TYPES.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab === tab.key
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search banners..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-700 font-medium"
              />
            </div>

            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active Only</option>
              <option value="INACTIVE">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Banners List / Grid */}
      {loading ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
          <RefreshCw size={36} className="animate-spin text-indigo-500 mx-auto mb-3" />
          <p className="text-slate-500 font-medium text-sm">Loading banners...</p>
        </div>
      ) : filteredBanners.length === 0 ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
          <ImageIcon size={48} className="text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-700">No Banners Found</h3>
          <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">
            {searchQuery ? 'No banners match your search filters.' : 'Click "Upload New Banner" to add home and profile banners for the mobile app.'}
          </p>
          <button
            onClick={() => handleOpenCreateModal(activeTab)}
            className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus size={16} /> Upload Banner
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredBanners.map(banner => {
            const resolvedImg = resolveAssetUrl(banner.image_url);
            const isHome = banner.banner_type === 'HOME';

            return (
              <div
                key={banner.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition group"
              >
                {/* Media Preview Container */}
                <div className="relative w-full h-44 bg-slate-900 flex items-center justify-center overflow-hidden">
                  {resolvedImg ? (
                    <img
                      src={resolvedImg}
                      alt={banner.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => setPreviewModalUrl(resolvedImg)}
                      onError={(e) => {
                        const current = e.target.src;
                        if (current.includes('http://')) {
                          e.target.src = current.replace('http://', 'https://');
                        } else if (current.includes('sandbox.xoxolite.com/uploads/') && !current.includes('/xoxo/')) {
                          e.target.src = current.replace('sandbox.xoxolite.com/uploads/', 'sandbox.xoxolite.com/xoxo/uploads/');
                        }
                      }}
                    />
                  ) : (
                    <div className="text-slate-400 flex flex-col items-center">
                      <ImageIcon size={32} />
                      <span className="text-xs mt-1">No Image</span>
                    </div>
                  )}

                  {/* Type Badge */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider shadow-sm ${isHome
                          ? 'bg-blue-600 text-white'
                          : 'bg-purple-600 text-white'
                        }`}
                    >
                      {isHome ? 'Home Screen' : 'Profile Bottom'}
                    </span>

                    {banner.image_url && banner.image_url.toLowerCase().endsWith('.gif') && (
                      <span className="px-2 py-0.5 rounded-lg text-xs font-black bg-pink-500 text-white shadow-sm">
                        GIF
                      </span>
                    )}
                  </div>

                  {/* Status Indicator */}
                  <div className="absolute top-3 right-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm backdrop-blur-md ${banner.is_active
                          ? 'bg-emerald-500/90 text-white'
                          : 'bg-slate-700/90 text-slate-200'
                        }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${banner.is_active ? 'bg-white' : 'bg-slate-400'}`}></span>
                      {banner.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Preview Click Overlay */}
                  {resolvedImg && (
                    <button
                      onClick={() => setPreviewModalUrl(resolvedImg)}
                      className="absolute bottom-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition"
                      title="View Full Preview"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-slate-800 text-base line-clamp-1">{banner.title}</h3>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                        Order #{banner.sort_order}
                      </span>
                    </div>

                    {banner.description && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{banner.description}</p>
                    )}

                    {/* Action Meta */}
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <span className="font-semibold text-slate-600">Action:</span>
                      <span className="px-2 py-0.5 rounded bg-slate-100 font-mono text-slate-700 text-[11px]">
                        {banner.action_type || 'NONE'}
                      </span>
                    </div>

                    {(banner.link_url || banner.target_id) && (
                      <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                        <span className="font-semibold text-slate-600">Target:</span>
                        <span className="truncate max-w-[180px] font-mono text-indigo-600 text-[11px]" title={banner.link_url || banner.target_id}>
                          {banner.link_url || banner.target_id}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions Bar */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <button
                      onClick={() => handleToggleStatus(banner)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-xl transition ${banner.is_active
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}
                    >
                      {banner.is_active ? 'Deactivate' : 'Activate'}
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEditModal(banner)}
                        className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                        title="Edit Banner"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteBanner(banner)}
                        className="p-1.5 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete Banner"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-100 relative my-8">
            <h2 className="text-xl font-bold text-slate-800">
              {editBannerId ? 'Edit Banner' : 'Upload New Banner'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Select image or GIF file. Visual formats will be visible in the mobile app.
            </p>

            {modalError && (
              <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
                <AlertCircle size={16} className="shrink-0" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              {/* Type Select */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Banner Location</label>
                <div className="grid grid-cols-2 gap-3 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, banner_type: 'HOME' })}
                    className={`p-3 rounded-xl border text-sm font-bold flex items-center justify-center gap-2 transition ${formData.banner_type === 'HOME'
                        ? 'border-indigo-600 bg-indigo-50/70 text-indigo-700 ring-2 ring-indigo-500/20'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                  >
                    <ImageIcon size={18} />
                    <span>Home Screen Banner</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, banner_type: 'PROFILE' })}
                    className={`p-3 rounded-xl border text-sm font-bold flex items-center justify-center gap-2 transition ${formData.banner_type === 'PROFILE'
                        ? 'border-purple-600 bg-purple-50/70 text-purple-700 ring-2 ring-purple-500/20'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                  >
                    <Sliders size={18} />
                    <span>Profile Bottom Banner</span>
                  </button>
                </div>
              </div>

              {/* Title & Description */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Grand Weekend Event"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-700 font-semibold"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Short caption or details..."
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-700"
                />
              </div>

              {/* Media File Upload */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Banner Image / GIF {editBannerId ? '(Leave empty to keep existing)' : '*'}
                </label>

                <div className="mt-1.5 p-4 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 text-center hover:bg-slate-100 transition relative">
                  <input
                    type="file"
                    accept="image/*,.gif,.svga,.webp,.svg,.apng"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center justify-center gap-1.5">
                    <Upload size={24} className="text-indigo-600" />
                    <span className="text-xs font-bold text-slate-700">
                      {formData.file ? formData.file.name : 'Click or drag file here to upload'}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      Supports JPG, PNG, GIF, WebP, SVG, SVGA, APNG (PDF/DOC not allowed)
                    </span>
                  </div>
                </div>

                {/* Previews */}
                {(formData.file || formData.existing_image) && (
                  <div className="mt-3 p-3 bg-slate-100 rounded-xl flex items-center gap-3">
                    <div className="w-16 h-12 bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                      <img
                        src={formData.file ? URL.createObjectURL(formData.file) : resolveAssetUrl(formData.existing_image)}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-xs">
                      <p className="font-bold text-slate-700">
                        {formData.file ? 'Selected New File' : 'Current Banner Image'}
                      </p>
                      <p className="text-slate-500 text-[11px]">
                        {formData.file ? `${(formData.file.size / 1024).toFixed(1)} KB` : formData.existing_image}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Type & Links */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Action Type</label>
                  <select
                    value={formData.action_type}
                    onChange={e => setFormData({ ...formData, action_type: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none"
                  >
                    {ACTION_TYPES.map(act => (
                      <option key={act.key} value={act.key}>{act.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none"
                  />
                </div>
              </div>

              {formData.action_type === 'EXTERNAL_URL' && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">External Web URL</label>
                  <input
                    type="url"
                    placeholder="https://example.com/event"
                    value={formData.link_url}
                    onChange={e => setFormData({ ...formData, link_url: e.target.value })}
                    className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-700"
                  />
                </div>
              )}

              {['ROOM', 'PROFILE', 'STORE'].includes(formData.action_type) && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {formData.action_type === 'ROOM' ? 'Target Room ID' : formData.action_type === 'PROFILE' ? 'Target User ID' : 'Target Item ID'}
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 1004"
                    value={formData.target_id}
                    onChange={e => setFormData({ ...formData, target_id: e.target.value })}
                    className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-700"
                  />
                </div>
              )}

              {/* Active Toggle */}
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="banner_is_active"
                  checked={formData.is_active}
                  onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <label htmlFor="banner_is_active" className="text-sm font-bold text-slate-700 cursor-pointer">
                  Activate banner immediately on mobile app
                </label>
              </div>

              {/* Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={modalLoading}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold shadow-md shadow-indigo-200 hover:from-indigo-700 hover:to-purple-700 transition flex items-center gap-2"
                >
                  {modalLoading && <RefreshCw size={16} className="animate-spin" />}
                  <span>{editBannerId ? 'Save Changes' : 'Upload Banner'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Full Preview Modal */}
      {previewModalUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setPreviewModalUrl(null)}
        >
          <div className="relative max-w-3xl max-h-[85vh] bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
            <button
              onClick={() => setPreviewModalUrl(null)}
              className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/90 text-white rounded-full transition z-10"
            >
              <XCircle size={22} />
            </button>
            <img
              src={resolveAssetUrl(previewModalUrl)}
              alt="Full Preview"
              className="w-full h-auto max-h-[80vh] object-contain"
              onError={(e) => {
                const current = e.target.src;
                if (current.includes('http://')) {
                  e.target.src = current.replace('http://', 'https://');
                } else if (current.includes('sandbox.xoxolite.com/uploads/') && !current.includes('/xoxo/')) {
                  e.target.src = current.replace('sandbox.xoxolite.com/uploads/', 'sandbox.xoxolite.com/xoxo/uploads/');
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BannersManagement;
